(() => {
  const SELECTOR = '[data-leile-search]';

  class LeileSearch {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.panel = root.querySelector('.leile-search__panel');
      this.input = root.querySelector('[data-leile-search-input]');
      this.results = root.querySelector('[data-leile-search-results]');
      this.initialResults = this.results.innerHTML;
      this.status = root.querySelector('[data-leile-search-status]');
      this.heading = root.querySelector('[data-leile-search-heading]');
      this.clear = root.querySelector('[data-leile-search-clear]');
      this.viewAll = root.querySelector('[data-leile-search-view-all]');
      this.header = this.findHeader();
      this.timer = null;
      this.controller = null;
      this.open = false;
      this.bind();
    }

    findHeader() {
      const section = document.getElementById(`shopify-section-${this.sectionId}`);
      return section && (section.querySelector('[data-leile-header]') || section.querySelector('#header') || section.querySelector('header'));
    }

    triggers() {
      if (!this.header) return [];
      return [...this.header.querySelectorAll('[data-leile-search-open], #dT_TopStickySearchBtn, .header__search, a[href*="/search"]')];
    }

    bind() {
      this.triggers().forEach((trigger) => {
        trigger.setAttribute('aria-controls', this.root.id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.show(trigger);
        }, true);
      });
      this.root.querySelectorAll('[data-leile-search-close]').forEach((button) => button.addEventListener('click', () => this.hide()));
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.clear.hidden = true;
        this.reset();
        this.input.focus();
      });
      this.input.addEventListener('input', () => this.onInput());
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.open) this.hide();
      });
      window.addEventListener('resize', () => this.open && this.position(), { passive: true });
      window.addEventListener('scroll', () => this.open && this.position(), { passive: true });
      document.addEventListener('shopify:section:load', () => this.position());
    }

    show(trigger) {
      document.querySelectorAll(`${SELECTOR}.is-open`).forEach((openRoot) => {
        if (openRoot !== this.root && openRoot._leileSearch) openRoot._leileSearch.hide();
      });
      this.trigger = trigger;
      this.root.hidden = false;
      this.position();
      requestAnimationFrame(() => this.root.classList.add('is-open'));
      this.triggers().forEach((item) => item.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('leile-search-open');
      this.open = true;
      setTimeout(() => this.input.focus(), 120);
    }

    hide() {
      if (!this.open) return;
      this.root.classList.remove('is-open');
      this.triggers().forEach((item) => item.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('leile-search-open');
      this.open = false;
      setTimeout(() => { if (!this.open) this.root.hidden = true; }, 280);
      if (this.trigger) this.trigger.focus({ preventScroll: true });
    }

    position() {
      this.header = this.findHeader() || this.header;
      if (!this.header) return;
      const rect = this.header.getBoundingClientRect();
      const mobile = matchMedia('(max-width: 749px)').matches;
      const gap = Number(mobile ? this.root.dataset.gapMobile : this.root.dataset.gapDesktop) || 16;
      const align = this.root.dataset.alignment;
      let left = rect.left + rect.width / 2;
      if (align === 'left') left = rect.left;
      if (align === 'right') left = rect.right;
      this.root.style.setProperty('--ls-top', `${Math.max(8, rect.bottom + gap)}px`);
      this.root.style.setProperty('--ls-left', `${left}px`);
    }

    onInput() {
      const query = this.input.value.trim();
      this.clear.hidden = !query;
      this.viewAll.href = `${this.root.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`;
      clearTimeout(this.timer);
      if (query.length < 2) return this.reset();
      this.timer = setTimeout(() => this.search(query), 220);
    }

    reset() {
      if (this.controller) this.controller.abort();
      this.results.innerHTML = this.initialResults;
      this.status.textContent = '';
      this.heading.textContent = this.heading.dataset.initial || this.heading.textContent;
    }

    async search(query) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.status.textContent = 'Searching…';
      const limit = Math.max(1, Math.min(10, Number(this.root.dataset.limit) || 4));
      try {
        const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${limit}&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { signal: this.controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search request failed');
        const json = await response.json();
        const products = json.resources?.results?.products || [];
        this.render(products);
        this.status.textContent = `${products.length} result${products.length === 1 ? '' : 's'}`;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.results.innerHTML = '<p class="leile-search__empty">Search is temporarily unavailable. Press Enter to view results.</p>';
          this.status.textContent = '';
        }
      }
    }

    escape(value = '') {
      const div = document.createElement('div');
      div.textContent = value;
      return div.innerHTML;
    }

    render(products) {
      if (!products.length) {
        this.results.innerHTML = '<p class="leile-search__empty">No matching products found.</p>';
        return;
      }
      this.results.innerHTML = products.map((product) => {
        const image = product.image ? `<img src="${this.escape(product.image)}&width=180" alt="" loading="lazy">` : '';
        const price = product.price ? `<span class="leile-search__price">${this.escape(product.price)}</span>` : '';
        return `<a class="leile-search__product" href="${this.escape(product.url)}"><span class="leile-search__media">${image}</span><span class="leile-search__copy"><strong>${this.escape(product.title)}</strong>${price}</span><span class="leile-search__arrow" aria-hidden="true">↗</span></a>`;
      }).join('');
    }
  }

  const init = (scope = document) => scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root._leileSearch) return;
    root._leileSearch = new LeileSearch(root);
  });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => init()) : init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
